import { useEffect, useState, useRef } from "react";
import { AiFillEyeInvisible, AiFillEye } from "react-icons/ai";
import { FaGoogle, FaFacebook } from "react-icons/fa";
import {
  FaBarsStaggered,
  FaXmark,
  FaEnvelope,
} from "react-icons/fa6";
import { FaUserCircle } from "react-icons/fa";
import cartIcon from "../assets/shopping-cart-svgrepo-com (1).svg";
import Dashboard from "./Dashboard";

import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";

import {
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

import { db } from "../firebase";
import {
  toast,
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import { useMyContext } from "../Context/MyContext";
import Cart from "./Cart";
import SearchBar from "./SearchBar";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPassword, setShowPassword] =
    useState(false);

  const [showSignUpPassword, setShowSignUpPassword] =
    useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [
    resetPasswordLoading,
    setResetPasswordLoading,
  ] = useState(false);

  const [currentUser, setCurrentUser] =
    useState(null);

  // Search dropdown and detailed modal item
  const [selectedItem, setSelectedItem] =
    useState(null);

  const [loadingItemId, setLoadingItemId] =
    useState(null);

  // Detailed modal variants and quantity
  const [activeColor, setActiveColor] =
    useState(null);

  const [activeSize, setActiveSize] =
    useState(null);

  const [selectedQty, setSelectedQty] =
    useState(1);

  const [
    lastVariantSelection,
    setLastVariantSelection,
  ] = useState(null);

  const [savedScroll, setSavedScroll] =
    useState(0);

  const [signUpName, setSignUpName] =
    useState("");

  const [signUpEmail, setSignUpEmail] =
    useState("");

  const [signUpPassword, setSignUpPassword] =
    useState("");

  const [signUpLoading, setSignUpLoading] =
    useState(false);

  const [showDashboard, setShowDashboard] =
    useState(false);

  // Inbox modal and selected note
  const [showInbox, setShowInbox] =
    useState(false);

  const {
    cart,
    toggleCart,
    isCartOpen,
    capitalizeWords,
    searchQuery,
    setSearchQuery,
    filteredCookingItems,
    addToCart,
    showSignInModal,
    setShowSignInModal,
    showSignUpModal,
    setShowSignUpModal,
    notes,
    isAdmin,
  } = useMyContext();

  const auth = getAuth();

  // Helpers
  const normalize = (value) =>
    (value || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");

  const getName = (maybeObjOrString) =>
    typeof maybeObjOrString === "string"
      ? maybeObjOrString
      : maybeObjOrString?.name ?? null;

  const hasVariants = (item) =>
    (Array.isArray(item?.colors) &&
      item.colors.length > 0) ||
    (Array.isArray(item?.sizes) &&
      item.sizes.length > 0);

  const toNum = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return null;
    }

    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : null;
  };

  const getVariantPrice = (
    item,
    colorName,
    sizeName,
    lastSelection = null,
  ) => {
    if (!item) return 0;

    const basePrice =
      toNum(item.priceDiscounted) ??
      toNum(item.priceOriginal) ??
      0;

    if (!hasVariants(item)) {
      return basePrice;
    }

    const sizeObj =
      sizeName &&
      (item.sizes || []).find(
        (size) =>
          getName(size) === sizeName,
      );

    const colorObj =
      colorName &&
      (item.colors || []).find(
        (color) =>
          getName(color) === colorName,
      );

    const sizePrice =
      sizeObj &&
      (toNum(sizeObj.priceDiscounted) ??
        toNum(sizeObj.priceOriginal));

    const colorPrice =
      colorObj &&
      (toNum(colorObj.priceDiscounted) ??
        toNum(colorObj.priceOriginal));

    if (lastSelection === "size") {
      return (
        sizePrice ??
        colorPrice ??
        basePrice
      );
    }

    if (lastSelection === "color") {
      return (
        colorPrice ??
        sizePrice ??
        basePrice
      );
    }

    return (
      sizePrice ??
      colorPrice ??
      basePrice
    );
  };

  const getImageForVariant = (
    item,
    colorName,
    sizeName,
    lastSelection = null,
  ) => {
    if (!item) {
      return "/placeholder.jpg";
    }

    const color = colorName
      ? String(colorName).trim()
      : null;

    const size = sizeName
      ? String(sizeName).trim()
      : null;

    const sizeUrl = size
      ? item?.sizeImages?.[size]
      : null;

    const colorUrl = color
      ? item?.colorImages?.[color]
      : null;

    if (lastSelection === "size") {
      return (
        sizeUrl ||
        colorUrl ||
        item.image ||
        "/placeholder.jpg"
      );
    }

    if (lastSelection === "color") {
      return (
        colorUrl ||
        sizeUrl ||
        item.image ||
        "/placeholder.jpg"
      );
    }

    return (
      sizeUrl ||
      colorUrl ||
      item.image ||
      "/placeholder.jpg"
    );
  };

  const clampQtyToStock = (
    quantity,
    stock,
  ) => {
    const parsedQuantity = Number(
      quantity || 1,
    );

    const parsedStock = Number(stock || 0);

    if (
      !Number.isFinite(parsedQuantity) ||
      parsedQuantity <= 0
    ) {
      return 1;
    }

    if (
      !Number.isFinite(parsedStock) ||
      parsedStock <= 0
    ) {
      return 1;
    }

    return Math.min(
      parsedQuantity,
      parsedStock,
    );
  };

  // Approximate stock information used in Navbar
  const getApproxStock = (item) => {
    if (!item) return 0;

    const colorObject =
      activeColor &&
      (item.colors || []).find(
        (color) =>
          getName(color) === activeColor,
      );

    const sizeObject =
      activeSize &&
      (item.sizes || []).find(
        (size) =>
          getName(size) === activeSize,
      );

    const quantityFromVariant =
      Number(colorObject?.quantity || 0) ||
      Number(sizeObject?.quantity || 0) ||
      null;

    if (
      quantityFromVariant !== null &&
      quantityFromVariant !== 0
    ) {
      return quantityFromVariant;
    }

    return Number(item.quantity || 0);
  };

  // Authentication observer
  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        (user) => {
          setCurrentUser(user);
        },
      );

    return () => unsubscribe();
  }, [auth]);

  const toggleMenu = () => {
    setIsMenuOpen((current) => !current);
  };

  const handleToggleCart = () => {
    toggleCart();

    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  // Expandable note component
  const NoteItem = ({
    note,
    formatTimestamp,
  }) => {
    const [expanded, setExpanded] =
      useState(false);

    const {
      isAdmin: noteIsAdmin,
      notes: contextNotes,
      setNotes,
    } = useMyContext();

    const handleDeleteNote = async (
      event,
    ) => {
      event.stopPropagation();

      if (
        !window.confirm(
          "Are you sure you want to delete this note?",
        )
      ) {
        return;
      }

      try {
        await deleteDoc(
          doc(db, "Notes", note.id),
        );

        if (setNotes) {
          setNotes(
            contextNotes.filter(
              (currentNote) =>
                currentNote.id !== note.id,
            ),
          );
        }

        toast.success(
          "Note deleted successfully!",
        );
      } catch (error) {
        console.error(
          "Error deleting note:",
          error,
        );

        toast.error(
          "Failed to delete note",
        );
      }
    };

    return (
      <div
        className="border rounded-md cursor-pointer hover:bg-gray-50 transition-all duration-300 overflow-hidden"
        onClick={() =>
          setExpanded(
            (current) => !current,
          )
        }
      >
        <div className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div className="flex flex-col">
            <p className="font-semibold">
              {note.userName ||
                "Unknown Name"}
            </p>

            <p className="text-sm text-gray-600">
              {note.userEmail ||
                "No Email"}
            </p>

            <p className="text-sm text-gray-600">
              {note.customerPhone ||
                "No Phone"}
            </p>

            <p className="text-sm text-gray-600">
              {formatTimestamp(
                note.timestamp,
              )}
            </p>
          </div>
        </div>

        <div
          className={`px-4 text-gray-700 text-sm transition-all duration-300 ${
            expanded
              ? "max-h-96 opacity-100 pb-4"
              : "max-h-0 opacity-0"
          } overflow-hidden`}
        >
          {note.note}

          {noteIsAdmin && (
            <div
              className={`mt-4 flex justify-end transition-all duration-300 transform ${
                expanded
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <button
                type="button"
                onClick={handleDeleteNote}
                className="text-red-500 text-sm px-4 py-1.5 border border-red-500 rounded hover:bg-red-500 hover:text-white transition"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleScrollAdjust = (
    event,
    path,
  ) => {
    event.preventDefault();

    if (isMenuOpen) {
      setIsMenuOpen(false);
    }

    if (path === "profile") {
      if (currentUser) {
        setShowDashboard(true);
      } else {
        setShowSignInModal(true);
      }

      return;
    }

    const navigateToSection = () => {
      const target =
        document.getElementById(path);

      if (!target) {
        console.warn(
          `Navigation target with id "${path}" was not found.`,
        );

        return;
      }

      const header =
        document.querySelector("header");

      const offset =
        header?.offsetHeight || 100;

      const top =
        target.getBoundingClientRect()
          .top +
        window.pageYOffset -
        offset;

      window.scrollTo({
        top: Math.max(top, 0),
        behavior: "smooth",
      });

      window.history.replaceState(
        null,
        "",
        `#${path}`,
      );
    };

    if (isMenuOpen) {
      window.setTimeout(
        navigateToSection,
        300,
      );
    } else {
      navigateToSection();
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();

    const receivedName = signUpName
      .replace(/\s+/g, " ")
      .trim();

    const receivedEmail = signUpEmail
      .trim()
      .toLowerCase();

    if (!receivedName) {
      toast.warn(
        "Please enter your full name.",
      );

      return;
    }

    if (receivedName.length < 2) {
      toast.warn(
        "Please enter a valid full name.",
      );

      return;
    }

    if (!receivedEmail) {
      toast.warn(
        "Please enter your email address.",
      );

      return;
    }

    if (!signUpPassword) {
      toast.warn(
        "Please enter a password.",
      );

      return;
    }

    if (signUpPassword.length < 6) {
      toast.warn(
        "Password must contain at least 6 characters.",
      );

      return;
    }

    try {
      setSignUpLoading(true);

      const {
        createUserWithEmailAndPassword,
        updateProfile,
      } = await import("firebase/auth");

      const {
        doc: firestoreDoc,
        setDoc,
        serverTimestamp,
      } = await import(
        "firebase/firestore"
      );

      const { db: firestoreDb } =
        await import("../firebase");

      const userCredential =
        await createUserWithEmailAndPassword(
          auth,
          receivedEmail,
          signUpPassword,
        );

      const user = userCredential.user;

      await updateProfile(user, {
        displayName: receivedName,
      });

      const userDocumentReference =
        firestoreDoc(
          firestoreDb,
          "charex",
          "main",
          "users",
          user.uid,
        );

      await setDoc(
        userDocumentReference,
        {
          uid: user.uid,
          name: receivedName,
          displayName: receivedName,
          email: receivedEmail,
          role: "customer",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        {
          merge: true,
        },
      );

      setCurrentUser({
        ...user,
        displayName: receivedName,
      });

      toast.success(
        `Welcome ${receivedName}! Your account was created successfully.`,
      );

      setSignUpName("");
      setSignUpEmail("");
      setSignUpPassword("");
      setShowSignUpPassword(false);
      setShowSignUpModal(false);
    } catch (error) {
      console.error(
        "Sign-up error:",
        error,
      );

      const errorCode = error?.code;

      if (
        errorCode ===
        "auth/email-already-in-use"
      ) {
        toast.error(
          "An account already exists with this email address. Please sign in instead.",
        );
      } else if (
        errorCode ===
        "auth/invalid-email"
      ) {
        toast.error(
          "Please enter a valid email address.",
        );
      } else if (
        errorCode ===
        "auth/weak-password"
      ) {
        toast.error(
          "Password must contain at least 6 characters.",
        );
      } else if (
        errorCode ===
        "auth/network-request-failed"
      ) {
        toast.error(
          "Network error. Please check your internet connection and try again.",
        );
      } else if (
        errorCode ===
        "permission-denied"
      ) {
        toast.error(
          "The account was created, but Firestore blocked the user profile. Check your Firestore security rules.",
        );
      } else {
        toast.error(
          error?.message ||
            "Account creation failed. Please try again.",
        );
      }
    } finally {
      setSignUpLoading(false);
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();

    try {
      const userCredential =
        await signInWithEmailAndPassword(
          auth,
          email,
          password,
        );

      if (userCredential.user) {
        toast.success(
          "Signed in successfully!",
        );

        setShowSignInModal(false);
      }
    } catch {
      toast.error(
        "Bad user credentials",
      );
    }
  };

  // Fixed password-reset handler
  const handleForgotPassword = async (
    event,
  ) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    const resetEmail = email.trim();

    if (!resetEmail) {
      toast.warn(
        "Please enter your email address first.",
      );

      return;
    }

    const emailPattern =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(resetEmail)) {
      toast.warn(
        "Please enter a valid email address.",
      );

      return;
    }

    try {
      setResetPasswordLoading(true);

      /*
        No ActionCodeSettings or continue URL is passed here.
        Firebase will use its standard password-reset page.
        This prevents auth/unauthorized-continue-uri.
      */
      await sendPasswordResetEmail(
        auth,
        resetEmail,
      );

      toast.success(
        `Password reset link sent to ${resetEmail}. Please check your inbox or spam folder.`,
      );
    } catch (error) {
      console.error(
        "Password reset error:",
        error,
      );

      const errorCode = error?.code;

      if (
        errorCode ===
        "auth/invalid-email"
      ) {
        toast.error(
          "Please enter a valid email address.",
        );
      } else if (
        errorCode ===
        "auth/too-many-requests"
      ) {
        toast.error(
          "Too many attempts. Please wait and try again later.",
        );
      } else if (
        errorCode ===
        "auth/network-request-failed"
      ) {
        toast.error(
          "Network error. Please check your internet connection.",
        );
      } else if (
        errorCode ===
        "auth/unauthorized-continue-uri"
      ) {
        toast.error(
          "This website domain has not been authorized in Firebase.",
        );
      } else {
        toast.error(
          error?.message ||
            "Failed to send password reset link. Please try again.",
        );
      }
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    toast.info(
      "Google sign-in is not available now. Please try again later.",
    );
  };

  const handleFacebookSignIn =
    async () => {
      try {
        const {
          FacebookAuthProvider,
          signInWithPopup,
        } = await import(
          "firebase/auth"
        );

        const provider =
          new FacebookAuthProvider();

        const result =
          await signInWithPopup(
            auth,
            provider,
          );

        toast.success(
          `Welcome, ${
            result.user.displayName ||
            "Guest"
          }!`,
        );

        setShowSignInModal(false);
      } catch (error) {
        console.error(
          "Facebook Sign-in error:",
          error,
        );

        toast.error(
          "Facebook sign-in failed",
        );
      }
    };

  const handleLogout = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }

    signOut(auth)
      .then(() =>
        toast.info("Logged out"),
      )
      .catch(() =>
        toast.error("Logout failed"),
      );
  };

  // Detailed Add to Cart from modal
  const handleAddToCartDetailed =
    async (item) => {
      if (!item) return;

      setLoadingItemId(item.id);

      try {
        await new Promise((resolve) =>
          setTimeout(resolve, 200),
        );

        const itemHasVariants =
          hasVariants(item);

        const chosenColor =
          itemHasVariants &&
          Array.isArray(item.colors) &&
          item.colors.length > 0
            ? activeColor ||
              getName(item.colors[0])
            : null;

        const chosenSize =
          itemHasVariants &&
          Array.isArray(item.sizes) &&
          item.sizes.length > 0
            ? activeSize ||
              getName(item.sizes[0])
            : null;

        const approximateStock =
          getApproxStock(item);

        if (approximateStock > 0) {
          const clamped =
            clampQtyToStock(
              selectedQty,
              approximateStock,
            );

          if (clamped !== selectedQty) {
            setSelectedQty(clamped);

            toast.info(
              `Only ${approximateStock} left for this selection.`,
            );

            return;
          }
        }

        const resolvedPrice =
          itemHasVariants
            ? getVariantPrice(
                item,
                chosenColor,
                chosenSize,
                lastVariantSelection,
              )
            : Number(
                item.priceDiscounted,
              ) ??
              Number(
                item.priceOriginal,
              ) ??
              null;

        if (resolvedPrice === null) {
          toast.error(
            "Price could not be resolved for this item.",
          );

          return;
        }

        const variantImage =
          itemHasVariants
            ? getImageForVariant(
                item,
                chosenColor,
                chosenSize,
                lastVariantSelection,
              )
            : item.image;

        const variantId =
          itemHasVariants
            ? [
                item.id,
                chosenColor,
                chosenSize,
              ]
                .filter(Boolean)
                .join("-")
            : item.id;

        const variants =
          itemHasVariants &&
          (chosenColor || chosenSize)
            ? {
                ...(chosenColor
                  ? {
                      color:
                        chosenColor,
                    }
                  : {}),
                ...(chosenSize
                  ? {
                      size:
                        chosenSize,
                    }
                  : {}),
              }
            : null;

        const variantText =
          variants &&
          Object.keys(variants).length
            ? Object.entries(variants)
                .map(
                  ([key, value]) =>
                    `${key}: ${value}`,
                )
                .join(", ")
            : "";

        const itemWithVariant = {
          ...item,
          id: variantId,
          parentId: item.id,

          ...(chosenColor
            ? {
                selectedColor:
                  chosenColor,
              }
            : {}),

          ...(chosenSize
            ? {
                selectedSize:
                  chosenSize,
              }
            : {}),

          ...(variants
            ? { variants }
            : {}),

          variantText,
          variantImage,
          image: variantImage,

          quantity:
            Number(selectedQty) || 1,

          addedAt: Date.now(),

          status:
            item.status ||
            "Available",

          variantPrice:
            resolvedPrice,

          price: resolvedPrice,
        };

        addToCart(
          itemWithVariant,
          resolvedPrice,
        );

        toast.success(
          `✅ ${item.name} added to cart (${
            Number(selectedQty) || 1
          }${
            itemHasVariants
              ? ` • ${
                  variantText ||
                  "variant"
                }`
              : ""
          })`,
        );

        setSelectedItem(null);
        setLastVariantSelection(null);
        setActiveColor(null);
        setActiveSize(null);
        setSelectedQty(1);

        setTimeout(() => {
          try {
            window.scrollTo({
              top: savedScroll,
              behavior: "smooth",
            });
          } catch {
            // Ignore scroll errors
          }
        }, 250);
      } catch (error) {
        console.error(
          "Add to cart error:",
          error,
        );

        toast.error(
          "Something went wrong while adding to cart",
        );
      } finally {
        setLoadingItemId(null);
      }
    };

  const toggleVisibility = async (
    item,
  ) => {
    try {
      await updateDoc(
        doc(
          db,
          "charex",
          item.id,
        ),
        {
          available: !item.available,
        },
      );

      toast.info(
        `Product is now ${
          !item.available
            ? "visible"
            : "hidden"
        } to visitors`,
      );
    } catch {
      toast.error(
        "Failed to toggle visibility",
      );
    }
  };

  const confirmDeleteToast = async (
    item,
  ) => {
    if (
      window.confirm(
        "Are you sure you want to delete this item?",
      )
    ) {
      await deleteDoc(
        doc(
          db,
          "charex",
          item.id,
        ),
      );

      toast.success("Item deleted");
      setSelectedItem(null);
    }
  };

  const navItems = [
    {
      link: "Home",
      path: "home",
    },
    {
      link: "About",
      path: "about",
    },
    {
      link: "FAQ",
      path: "faq",
    },
    {
      link: "Profile",
      path: "profile",
    },
    {
      link: "Contact",
      path: "contact",
    },
  ];

  const username =
    currentUser?.email?.split("@")[0];

  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (
      event,
    ) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(
          event.target,
        )
      ) {
        setSelectedItem(null);
        setLastVariantSelection(null);
        setActiveColor(null);
        setActiveSize(null);
        setSelectedQty(1);
      }
    };

    if (selectedItem) {
      document.addEventListener(
        "mousedown",
        handleClickOutside,
      );
    } else {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
    }

    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
  }, [selectedItem]);

  const formatTimestamp = (
    timestamp,
  ) => {
    if (!timestamp) return "";

    try {
      if (
        typeof timestamp.toDate ===
        "function"
      ) {
        return timestamp
          .toDate()
          .toLocaleString();
      }

      if (timestamp.seconds) {
        return new Date(
          timestamp.seconds * 1000,
        ).toLocaleString();
      }

      return new Date(
        timestamp,
      ).toLocaleString();
    } catch {
      return "";
    }
  };

  const userIsAdmin =
    Boolean(isAdmin) ||
    Boolean(
      currentUser?.email &&
        currentUser.email
          .toLowerCase()
          .includes("admin"),
    );

  const openFromSearch = (item) => {
    if (!item) return;

    const isPreOrder =
      normalize(item?.status) ===
      "preorder";

    if (
      !userIsAdmin &&
      item?.available === false &&
      !isPreOrder
    ) {
      toast.info(
        "This product is currently hidden.",
      );

      return;
    }

    setSavedScroll(window.scrollY);
    setSelectedItem(item);

    if (
      Array.isArray(item?.colors) &&
      item.colors.length > 0
    ) {
      setActiveColor(
        getName(item.colors[0]) ||
          null,
      );
    } else {
      setActiveColor(null);
    }

    setActiveSize(null);
    setSelectedQty(1);
    setLastVariantSelection(null);
    setSearchQuery("");
  };

  return (
    <>
      <ToastContainer />

      <header className="fixed w-full z-50 transition-all duration-300 bg-white">
        <nav className="py-6 lg:px-24 px-4 bg-white shadow-lg relative overflow-visible">
          <div className="flex justify-between items-center text-base relative">
            <button
              type="button"
              onClick={toggleMenu}
              className="flex items-center gap-2 text-gray-600 p-2 border border-gray-500 rounded-full md:hidden"
            >
              {isMenuOpen ? (
                <FaXmark className="h-6 w-6" />
              ) : (
                <FaBarsStaggered className="h-6 w-6" />
              )}

              <span className="uppercase text-sm font-semibold">
                Menu
              </span>
            </button>

            <a
              href="/"
              className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center"
            >
              <img
                src="/images/charexLogo.png"
                alt="CHAREX Logo"
                className="h-20 md:h-24 lg:h-28 object-contain"
              />
            </a>

            <div className="flex items-center gap-3 relative">
              <div
                className="flex flex-col items-center cursor-pointer"
                onClick={() => {
                  if (currentUser) {
                    setShowDashboard(true);
                  } else {
                    setShowSignInModal(
                      true,
                    );
                  }
                }}
              >
                <FaUserCircle className="w-7 h-7 text-gray-600 hover:text-[#7b2cbf] transition" />

                <span className="text-xs text-gray-700 mt-1">
                  Account
                </span>
              </div>

              <div
                onClick={handleToggleCart}
                className="relative cursor-pointer text-gray-600"
              >
                <img
                  src={cartIcon}
                  alt="Cart"
                  className="w-6 h-6"
                />

                <span className="absolute -top-2 -right-2 bg-[#7b2cbf] text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
                  {cart.length > 0
                    ? cart.length
                    : 0}
                </span>
              </div>
            </div>

            <div className="hidden md:flex flex-1 items-center justify-end px-8 flex-wrap gap-6">
              <ul className="flex flex-wrap gap-6 items-center">
                {navItems.map(
                  ({ link, path }) => {
                    if (
                      path ===
                        "profile" &&
                      currentUser
                    ) {
                      return (
                        <div
                          key="user-info"
                          className="ml-4 flex flex-col items-start"
                        >
                          <span className="text-sm text-gray-700 flex items-center gap-1">
                            👤 {username}
                          </span>

                          <div className="flex items-center gap-2 mt-1">
                            {userIsAdmin && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowInbox(
                                    true,
                                  )
                                }
                                className="text-xs text-[#7b2cbf] hover:underline mr-2 flex items-center gap-2"
                                title="Open Inbox (admin)"
                              >
                                Inbox

                                <span className="bg-[#7b2cbf] text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
                                  {Array.isArray(
                                    notes,
                                  )
                                    ? notes.length
                                    : 0}
                                </span>
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={
                                handleLogout
                              }
                              className="text-xs text-red-500 hover:text-red-700"
                            >
                              Logout
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <a
                        key={link}
                        href={`#${path}`}
                        onClick={(
                          event,
                        ) =>
                          handleScrollAdjust(
                            event,
                            path,
                          )
                        }
                        className="relative pb-1 text-base uppercase text-[#181818] hover:text-[#7b2cbf] cursor-pointer transition-colors duration-300 after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-[#7b2cbf] after:transition-all after:duration-300 hover:after:w-full"
                      >
                        {link}
                      </a>
                    );
                  },
                )}
              </ul>
            </div>
          </div>

          <div className="w-full border-t mt-2 mb-2 border-gray-200" />

          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />

          {searchQuery &&
            filteredCookingItems.length >
              0 && (
              <div className="absolute left-0 right-0 mt-2 bg-white shadow-lg border rounded-lg z-40 max-h-80 overflow-y-auto">
                {filteredCookingItems.map(
                  (item) => (
                    <div
                      key={item.id}
                      onClick={() =>
                        openFromSearch(
                          item,
                        )
                      }
                      className="flex items-center gap-3 p-3 hover:bg-gray-100 cursor-pointer"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-14 h-14 rounded object-cover"
                      />

                      <div className="flex-1">
                        <h3 className="font-semibold">
                          {capitalizeWords(
                            item.name,
                          )}
                        </h3>

                        <p className="text-sm text-gray-500">
                          {item.category}
                        </p>

                        <p className="text-sm text-[#7b2cbf] font-semibold">
                          ₦
                          {Number(
                            item.priceDiscounted ??
                              item.priceOriginal ??
                              0,
                          ).toLocaleString()}
                        </p>

                        {item?.status && (
                          <p className="text-xs text-gray-500">
                            Status:{" "}
                            {
                              item.status
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

          {searchQuery &&
            filteredCookingItems.length ===
              0 && (
              <div className="absolute left-0 right-0 mt-2 bg-white border rounded-lg p-3 text-gray-500 text-center">
                No CHAREX products match your search.
              </div>
            )}

          <div className="w-full border-b mt-2 border-gray-200" />

          <div
            className={`fixed top-0 left-0 w-3/4 sm:w-1/2 h-screen bg-gradient-to-b from-[#f7f0ff] via-[#f3e8ff] to-white text-gray-800 border-r border-[#d8b4fe] shadow-2xl z-50 transform transition-transform duration-300 ${
              isMenuOpen
                ? "translate-x-0"
                : "-translate-x-full"
            }`}
          >
            <div className="flex justify-between items-center p-4 bg-[#efe2ff] border-b border-[#d8b4fe]">
              <a
                href="/"
                className="flex items-center"
              >
                <img
                  src="/images/charexLogo.png"
                  alt="CHAREX Logo"
                  className="h-12"
                />
              </a>

              <button
                type="button"
                onClick={toggleMenu}
                className="text-gray-800 p-2 border border-gray-500 rounded-full"
              >
                <FaXmark className="h-6 w-6" />
              </button>
            </div>

            <ul className="flex flex-col items-start mt-6 space-y-6 px-6">
              {navItems.map(
                ({ link, path }) => {
                  if (
                    path ===
                      "profile" &&
                    currentUser
                  ) {
                    return (
                      <button
                        key="user"
                        type="button"
                        onClick={(event) =>
                          handleScrollAdjust(
                            event,
                            "profile",
                          )
                        }
                        className="relative pb-1 text-base uppercase font-semibold flex items-center gap-1 hover:text-[#7b2cbf] transition-colors duration-300 after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-[#7b2cbf] after:transition-all after:duration-300 hover:after:w-full"
                      >
                        👤 {username}
                      </button>
                    );
                  }

                  return (
                    <a
                      key={link}
                      href={`#${path}`}
                      onClick={(
                        event,
                      ) =>
                        handleScrollAdjust(
                          event,
                          path,
                        )
                      }
                      className="relative pb-1 text-base uppercase font-medium text-gray-700 hover:text-[#7b2cbf] cursor-pointer transition-colors duration-300 after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-[#7b2cbf] after:transition-all after:duration-300 hover:after:w-full"
                    >
                      {link}
                    </a>
                  );
                },
              )}

              {currentUser &&
                userIsAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowInbox(true);
                      setIsMenuOpen(
                        false,
                      );
                    }}
                    className="relative flex items-center gap-2 pb-1 text-base uppercase font-medium text-gray-800 hover:text-[#7b2cbf] cursor-pointer transition-colors duration-300 after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-[#7b2cbf] after:transition-all after:duration-300 hover:after:w-full"
                  >
                    <FaEnvelope
                      size={18}
                      className="text-gray-700"
                    />

                    <span>Inbox</span>

                    {Array.isArray(
                      notes,
                    ) &&
                      notes.length >
                        0 && (
                        <span className="ml-2 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-md">
                          {
                            notes.length
                          }
                        </span>
                      )}
                  </button>
                )}

              {currentUser && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-red-500 hover:underline mt-2"
                >
                  Logout
                </button>
              )}
            </ul>
          </div>
        </nav>
      </header>

      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white shadow-lg z-50 transform transition-transform duration-300 ${
          isCartOpen
            ? "translate-x-0"
            : "translate-x-full"
        }`}
      >
        <Cart />
      </div>

      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            ref={modalRef}
            className="bg-white p-6 rounded-lg max-w-lg w-full relative shadow-xl overflow-y-auto max-h-[90vh] pb-[80px]"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => {
                setSelectedItem(null);
                setLastVariantSelection(
                  null,
                );
                setActiveColor(null);
                setActiveSize(null);
                setSelectedQty(1);

                setTimeout(() => {
                  try {
                    window.scrollTo({
                      top: savedScroll,
                      behavior:
                        "smooth",
                    });
                  } catch {
                    // Ignore scroll errors
                  }
                }, 250);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold mb-2 text-center">
              {capitalizeWords(
                selectedItem.name,
              )}
            </h2>

            <img
              src={getImageForVariant(
                selectedItem,
                activeColor ||
                  getName(
                    selectedItem
                      .colors?.[0],
                  ) ||
                  null,
                activeSize || null,
                lastVariantSelection,
              )}
              alt={selectedItem.name}
              className="w-full h-64 object-cover rounded mb-4"
            />

            <p className="mb-2 text-sm font-semibold">
              Status:{" "}
              <span
                className={`px-2 py-1 text-xs rounded text-white ${
                  normalize(
                    selectedItem.status,
                  ) === "available"
                    ? "bg-green-600"
                    : "bg-yellow-600"
                }`}
              >
                {selectedItem.status ||
                  "Available"}
              </span>
            </p>

            <p className="mb-2 text-sm italic text-gray-600">
              Category:{" "}
              {selectedItem.category ||
                "Uncategorized"}
            </p>

            {selectedItem.trackingStage && (
              <p className="mb-2 text-sm font-semibold">
                Tracking Stage:{" "}
                <span className="px-2 py-1 text-xs rounded bg-gray-500 text-white">
                  {
                    selectedItem.trackingStage
                  }
                </span>
              </p>
            )}

            <p className="mb-4 text-gray-700 leading-relaxed">
              {selectedItem.description ||
                "No description available."}
            </p>

            {Array.isArray(
              selectedItem.colors,
            ) &&
              selectedItem.colors.length >
                0 && (
                <div className="mb-3">
                  <p className="text-sm font-semibold mb-2">
                    Colors:
                  </p>

                  <select
                    value={
                      activeColor || ""
                    }
                    onChange={(
                      event,
                    ) => {
                      const nextColor =
                        event.target
                          .value ||
                        null;

                      setActiveColor(
                        nextColor,
                      );

                      setLastVariantSelection(
                        "color",
                      );
                    }}
                    className="w-full border px-3 py-2 rounded"
                  >
                    <option value="">
                      Select color
                    </option>

                    {selectedItem.colors.map(
                      (
                        color,
                        index,
                      ) => {
                        const name =
                          getName(
                            color,
                          ) ||
                          `color-${index}`;

                        const price =
                          color?.priceDiscounted ??
                          color?.priceOriginal ??
                          selectedItem.priceDiscounted ??
                          selectedItem.priceOriginal ??
                          null;

                        const labelPrice =
                          price
                            ? ` - ₦${Number(
                                price,
                              ).toLocaleString()}`
                            : "";

                        return (
                          <option
                            key={
                              name
                            }
                            value={
                              name
                            }
                          >
                            {name}
                            {
                              labelPrice
                            }
                          </option>
                        );
                      },
                    )}
                  </select>
                </div>
              )}

            {Array.isArray(
              selectedItem.sizes,
            ) &&
              selectedItem.sizes.length >
                0 && (
                <div className="mb-3">
                  <p className="text-sm font-semibold mb-2">
                    Sizes:
                  </p>

                  <select
                    value={
                      activeSize || ""
                    }
                    onChange={(
                      event,
                    ) => {
                      const nextSize =
                        event.target
                          .value ||
                        null;

                      setActiveSize(
                        nextSize,
                      );

                      setLastVariantSelection(
                        "size",
                      );
                    }}
                    className="w-full border px-3 py-2 rounded"
                  >
                    <option value="">
                      Select size
                    </option>

                    {selectedItem.sizes.map(
                      (
                        size,
                        index,
                      ) => {
                        const name =
                          getName(
                            size,
                          ) ||
                          `size-${index}`;

                        const price =
                          size?.priceDiscounted ??
                          size?.priceOriginal ??
                          selectedItem.priceDiscounted ??
                          selectedItem.priceOriginal ??
                          null;

                        const labelPrice =
                          price
                            ? ` - ₦${Number(
                                price,
                              ).toLocaleString()}`
                            : "";

                        return (
                          <option
                            key={
                              name
                            }
                            value={
                              name
                            }
                          >
                            {name}
                            {
                              labelPrice
                            }
                          </option>
                        );
                      },
                    )}
                  </select>
                </div>
              )}

            <p className="mb-2 text-gray-400 line-through">
              Original: ₦
              {Number(
                selectedItem.priceOriginal ??
                  0,
              ).toLocaleString()}
            </p>

            <p className="font-semibold text-[#7b2cbf] text-lg mb-2">
              Discount: ₦
              {Number(
                hasVariants(
                  selectedItem,
                )
                  ? getVariantPrice(
                      selectedItem,
                      activeColor,
                      activeSize,
                      lastVariantSelection,
                    )
                  : selectedItem.priceDiscounted ??
                      selectedItem.priceOriginal ??
                      0,
              ).toLocaleString()}
            </p>

            {selectedItem.shipmentFee && (
              <p className="text-sm text-gray-700 mb-4">
                🚚 Shipment Fee: ₦
                {Number(
                  selectedItem.shipmentFee,
                ).toLocaleString()}{" "}
                <span className="italic text-gray-500">
                  (paid separately)
                </span>
              </p>
            )}

            <p className="mb-3 text-sm font-semibold">
              Stock:{" "}
              <span className="px-2 py-1 text-xs rounded bg-gray-800 text-white">
                {getApproxStock(
                  selectedItem,
                )}
              </span>
            </p>

            <div className="mt-2 flex items-center gap-3">
              <p className="text-sm font-semibold">
                Quantity:
              </p>

              <button
                type="button"
                className="px-3 py-1 border rounded"
                onClick={() =>
                  setSelectedQty(
                    (quantity) =>
                      Math.max(
                        1,
                        Number(
                          quantity ||
                            1,
                        ) - 1,
                      ),
                  )
                }
              >
                -
              </button>

              <input
                type="number"
                min="1"
                value={selectedQty}
                onChange={(
                  event,
                ) => {
                  const stock =
                    getApproxStock(
                      selectedItem,
                    );

                  if (stock > 0) {
                    const next =
                      clampQtyToStock(
                        event.target
                          .value,
                        stock,
                      );

                    setSelectedQty(
                      next,
                    );

                    if (
                      Number(
                        event.target
                          .value,
                      ) > stock
                    ) {
                      toast.info(
                        `Only ${stock} left for this selection.`,
                      );
                    }
                  } else {
                    setSelectedQty(
                      Math.max(
                        1,
                        Number(
                          event.target
                            .value ||
                            1,
                        ),
                      ),
                    );
                  }
                }}
                className="w-20 border px-2 py-1 rounded text-center"
              />

              <button
                type="button"
                className="px-3 py-1 border rounded"
                onClick={() => {
                  const stock =
                    getApproxStock(
                      selectedItem,
                    );

                  setSelectedQty(
                    (quantity) => {
                      const next =
                        Number(
                          quantity ||
                            1,
                        ) + 1;

                      if (
                        stock > 0 &&
                        next > stock
                      ) {
                        toast.info(
                          `Only ${stock} left for this selection.`,
                        );

                        return stock;
                      }

                      return next;
                    },
                  );
                }}
              >
                +
              </button>

              <span className="text-xs text-gray-500 ml-auto">
                Max:{" "}
                {getApproxStock(
                  selectedItem,
                ) || "—"}
              </span>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              <button
                type="button"
                onClick={() =>
                  handleAddToCartDetailed(
                    selectedItem,
                  )
                }
                disabled={
                  loadingItemId ===
                  selectedItem.id
                }
                className={`px-4 py-2 rounded text-white ${
                  loadingItemId ===
                  selectedItem.id
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#7b2cbf] hover:bg-[#6923a6]"
                }`}
              >
                {loadingItemId ===
                selectedItem.id
                  ? "Adding..."
                  : `Add ${selectedQty} to Cart`}
              </button>

              {currentUser &&
                userIsAdmin && (
                  <div className="flex justify-end gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={(
                        event,
                      ) => {
                        event.stopPropagation();

                        toggleVisibility(
                          selectedItem,
                        );
                      }}
                      className="text-gray-600 hover:text-gray-800 flex items-center gap-2 border px-3 py-2 rounded"
                      title="Toggle visibility"
                    >
                      {selectedItem.available ? (
                        <>
                          <AiFillEye />
                          Visible
                        </>
                      ) : (
                        <>
                          <AiFillEyeInvisible />
                          Hidden
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        toast.info(
                          "Edit modal logic goes here",
                        )
                      }
                      className="bg-[#7b2cbf] text-white px-4 py-2 rounded hover:bg-[#6923a6]"
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        confirmDeleteToast(
                          selectedItem,
                        )
                      }
                      className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {showInbox &&
        userIsAdmin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
            <div className="bg-white p-4 md:p-6 rounded-md w-full max-w-[700px] relative max-h-[90vh] overflow-y-auto">
              <button
                type="button"
                onClick={() =>
                  setShowInbox(false)
                }
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>

              <h2 className="text-2xl font-bold mb-4 text-center">
                Inbox - Notes
              </h2>

              {Array.isArray(
                notes,
              ) &&
              notes.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {notes
                    .slice()
                    .sort(
                      (
                        firstNote,
                        secondNote,
                      ) => {
                        const firstTime =
                          firstNote
                            .timestamp
                            ?.seconds
                            ? firstNote
                                .timestamp
                                .seconds
                            : firstNote.timestamp
                              ? new Date(
                                  firstNote.timestamp,
                                ).getTime() /
                                1000
                              : 0;

                        const secondTime =
                          secondNote
                            .timestamp
                            ?.seconds
                            ? secondNote
                                .timestamp
                                .seconds
                            : secondNote.timestamp
                              ? new Date(
                                  secondNote.timestamp,
                                ).getTime() /
                                1000
                              : 0;

                        return (
                          secondTime -
                          firstTime
                        );
                      },
                    )
                    .map(
                      (note) => (
                        <NoteItem
                          key={note.id}
                          note={note}
                          formatTimestamp={
                            formatTimestamp
                          }
                        />
                      ),
                    )}
                </div>
              ) : (
                <p className="text-center text-gray-500">
                  No notes available.
                </p>
              )}
            </div>
          </div>
        )}

      {showSignInModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white p-6 rounded-md w-[90%] md:w-[400px] relative">
            <h2 className="text-2xl font-bold mb-4 text-center">
              Sign In
            </h2>

            <form
              onSubmit={handleSignIn}
            >
              <input
                type="email"
                placeholder="Email"
                className="w-full mb-4 p-2 border border-gray-300 rounded"
                value={email}
                onChange={(
                  event,
                ) =>
                  setEmail(
                    event.target.value,
                  )
                }
                required
              />

              <div className="relative mb-4">
                <input
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  placeholder="Password"
                  className="w-full p-2 border border-gray-300 rounded"
                  value={password}
                  onChange={(
                    event,
                  ) =>
                    setPassword(
                      event.target
                        .value,
                    )
                  }
                  required
                />

                {showPassword ? (
                  <AiFillEyeInvisible
                    className="absolute right-3 top-3 text-xl cursor-pointer"
                    onClick={() =>
                      setShowPassword(
                        (current) =>
                          !current,
                      )
                    }
                  />
                ) : (
                  <AiFillEye
                    className="absolute right-3 top-3 text-xl cursor-pointer"
                    onClick={() =>
                      setShowPassword(
                        (current) =>
                          !current,
                      )
                    }
                  />
                )}
              </div>

              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center text-sm text-gray-600">
                  <input
                    type="checkbox"
                    className="mr-2 accent-[#7b2cbf]"
                  />{" "}
                  Remember me
                </label>

                <button
                  type="button"
                  className={`text-sm text-[#7b2cbf] hover:underline ${
                    resetPasswordLoading
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  onClick={
                    handleForgotPassword
                  }
                  disabled={
                    resetPasswordLoading
                  }
                >
                  {resetPasswordLoading
                    ? "Sending link..."
                    : "Forgot password?"}
                </button>
              </div>

              <button
                type="submit"
                className="w-full bg-[#7b2cbf] text-white py-2 rounded hover:bg-[#6923a6] transition duration-200"
              >
                Sign In
              </button>

              <p className="text-sm text-center mt-2">
                Don&apos;t have an
                account?{" "}
                <span
                  onClick={() => {
                    setShowSignInModal(
                      false,
                    );

                    setShowSignUpModal(
                      true,
                    );
                  }}
                  className="text-[#7b2cbf] hover:underline cursor-pointer"
                >
                  Create one
                </span>
              </p>
            </form>

            <div className="flex flex-col gap-2 mt-4">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="flex items-center justify-center gap-2 w-full bg-[#7b2cbf] text-white py-2 rounded hover:bg-[#6923a6] transition"
              >
                <FaGoogle />
                Continue with Google
              </button>

              <button
                type="button"
                onClick={
                  handleFacebookSignIn
                }
                className="flex items-center justify-center gap-2 w-full bg-[#7b2cbf] text-white py-2 rounded hover:bg-[#6923a6] transition"
              >
                <FaFacebook />
                Continue with Facebook
              </button>
            </div>

            <button
              type="button"
              onClick={() =>
                setShowSignInModal(
                  false,
                )
              }
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {showDashboard &&
        currentUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white p-6 rounded-md w-[90%] md:w-[800px] relative max-h-[90vh] overflow-y-auto">
              <button
                type="button"
                onClick={() =>
                  setShowDashboard(
                    false,
                  )
                }
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>

              <Dashboard />
            </div>
          </div>
        )}

      {showSignUpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white p-6 rounded-md w-[90%] md:w-[400px] relative">
            <h2 className="text-2xl font-bold mb-4 text-center">
              Create Account
            </h2>

            <form
              onSubmit={handleSignUp}
            >
              <input
                type="text"
                name="fullName"
                id="charex-signup-full-name"
                placeholder="Full Name"
                autoComplete="name"
                className="w-full mb-4 p-2 border border-gray-300 rounded"
                value={signUpName}
                onChange={(event) => {
                  setSignUpName(
                    event.target.value,
                  );
                }}
                required
              />

              <input
                type="email"
                name="email"
                id="charex-signup-email"
                placeholder="Email"
                autoComplete="email"
                className="w-full mb-4 p-2 border border-gray-300 rounded"
                value={signUpEmail}
                onChange={(
                  event,
                ) =>
                  setSignUpEmail(
                    event.target.value,
                  )
                }
                required
              />

              <div className="relative mb-4">
                <input
                  type={
                    showSignUpPassword
                      ? "text"
                      : "password"
                  }
                  name="newPassword"
                  id="charex-signup-password"
                  placeholder="Password"
                  autoComplete="new-password"
                  minLength={6}
                  className="w-full p-2 pr-11 border border-gray-300 rounded"
                  value={signUpPassword}
                  onChange={(
                    event,
                  ) =>
                    setSignUpPassword(
                      event.target.value,
                    )
                  }
                  required
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowSignUpPassword(
                      (current) =>
                        !current,
                    )
                  }
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-xl text-gray-600 hover:text-[#7b2cbf] focus:outline-none"
                  aria-label={
                    showSignUpPassword
                      ? "Hide password"
                      : "Show password"
                  }
                  title={
                    showSignUpPassword
                      ? "Hide password"
                      : "Show password"
                  }
                >
                  {showSignUpPassword ? (
                    <AiFillEyeInvisible />
                  ) : (
                    <AiFillEye />
                  )}
                </button>
              </div>

              <button
                type="submit"
                disabled={signUpLoading}
                className={`w-full text-white py-2 rounded transition duration-200 ${
                  signUpLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#7b2cbf] hover:bg-[#6923a6]"
                }`}
              >
                {signUpLoading
                  ? "Creating Account..."
                  : "Sign Up"}
              </button>
            </form>

            <div className="flex flex-col gap-2 mt-4">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="flex items-center justify-center gap-2 w-full bg-[#7b2cbf] text-white py-2 rounded hover:bg-[#6923a6] transition"
              >
                <FaGoogle />
                Continue with Google
              </button>

              <button
                type="button"
                onClick={
                  handleFacebookSignIn
                }
                className="flex items-center justify-center gap-2 w-full bg-[#7b2cbf] text-white py-2 rounded hover:bg-[#6923a6] transition"
              >
                <FaFacebook />
                Continue with Facebook
              </button>
            </div>

            <p className="text-sm text-center mt-4">
              Already have an account?{" "}
              <span
                onClick={() => {
                  setShowSignUpModal(
                    false,
                  );

                  setShowSignInModal(
                    true,
                  );
                }}
                className="text-[#7b2cbf] hover:underline cursor-pointer"
              >
                Sign in
              </span>
            </p>

            <button
              type="button"
              onClick={() =>
                setShowSignUpModal(
                  false,
                )
              }
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;